<template>
  <v-dialog v-model="dialog.value" persistent max-width="800">
    <template v-slot:activator="{ on }">
      <v-btn class="mr-10" color="teal" dark v-on="on" @click="loadProperties">
        <v-icon left>mdi-plus</v-icon>
        Przypisz szkolenie
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">Przypisanie nowego szkolenia</span>
      </v-card-title>
      <v-card-text class="mt-4">
        <v-form ref="form" @submit.prevent="addAssignment">
          <v-autocomplete
            v-model="form.course"
            :loading="unAssignedCourses.loading"
            :items="unAssignedCourses.items"
            :search-input.sync="search"
            cache-items
            item-text="displayText"
            item-value="id"
            flat
            hide-no-data
            label="Szkolenie"
            :error-messages="courseErrors"
            @input="$v.form.course.$touch()"
            @blur="$v.form.course.$touch()"
          />
          <v-switch v-model="restrictDateFrom" class="mx-2" label="Ogranicz datą od" />
          <v-row v-if="restrictDateFrom">
            <v-col>
              <v-menu
                v-model="dateFromDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="dateFromFormatted"
                    label="Data rozpoczęcia"
                    readonly
                    :error-messages="dateFromDateErrors"
                    v-on="on"
                    @input="$v.form.dateFrom.$touch()"
                    @blur="$v.form.dateFrom.$touch()"
                    @keydown.enter="submit"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.dateFrom"
                  no-title
                  locale="pl-PL"
                  @input="dateFromDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col>
              <v-menu
                ref="menu1"
                v-model="dateFromTimeMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="form.dateFromTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="form.dateFromTime"
                    label="Godzina rozpoczęcia"
                    readonly
                    :error-messages="dateFromTimeErrors"
                    v-on="on"
                    @input="$v.form.dateFromTime.$touch()"
                    @blur="$v.form.dateFromTime.$touch()"
                    @keydown.enter="submit"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="dateFromTimeMenu"
                  v-model="form.dateFromTime"
                  full-width
                  format="24hr"
                  @click:minute="$refs.menu1.save(form.dateFromTime)"
                ></v-time-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-switch v-model="restrictDateTo" class="mx-2" label="Ogranicz datą do" />
          <v-row v-if="restrictDateTo">
            <v-col>
              <v-menu
                v-model="dateToDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="dateToFormatted"
                    label="Data zakończenia"
                    readonly
                    :error-messages="dateToDateErrors"
                    v-on="on"
                    @input="$v.form.dateTo.$touch()"
                    @blur="$v.form.dateTo.$touch()"
                    @keydown.enter="submit"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.dateTo"
                  no-title
                  locale="pl-PL"
                  @input="dateToDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col>
              <v-menu
                ref="menu2"
                v-model="dateToTimeMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="form.dateToTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="form.dateToTime"
                    label="Godzina zakończenia"
                    readonly
                    :error-messages="dateToTimeErrors"
                    v-on="on"
                    @input="$v.form.dateToTime.$touch()"
                    @blur="$v.form.dateToTime.$touch()"
                    @keydown.enter="submit"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="dateToTimeMenu"
                  v-model="form.dateToTime"
                  full-width
                  format="24hr"
                  @click:minute="$refs.menu2.save(form.dateToTime)"
                ></v-time-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row v-if="restrictDateTo">
            <v-col>              
              <v-checkbox
                v-model="form.keepCourseOngoing"
                class="mx-2"
                :label="form.keepCourseOngoing ? 'Czekaj z zakończeniem szkolenia do daty końca szkolenia' : 'Zakończ szkolenie po zdaniu wszystkich elementów'"
                hide-details
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn class="text--secondary" text :disabled="dialog.loading" @click="dialog.value = false">
          Anuluj
        </v-btn>
        <v-btn color="primary" text :loading="dialog.loading" @click="addAssignment">Przypisz</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { validationMixin } from 'vuelidate';
import * as moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { minDateTime } from '@/plugins/vuelidate/validators';

const { mapActions, mapState } = createNamespacedHelpers('groups');

export default {
  mixins: [validationMixin],
  props: {
    course: {
      type: Object,
      default: null
    },
    group: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      required: true
    }
  },
  data: function() {
    return {
      dialog: {
        value: false,
        loading: false
      },
      search: null,
      restrictDateFrom: false,
      restrictDateTo: false,
      form: {
        course: null,
        dateFrom: null,
        dateFromTime: null,
        dateTo: null,
        dateToTime: null,
        keepCourseOngoing: null
      },
      dateFromDateMenu: false,
      dateFromTimeMenu: false,
      dateToDateMenu: false,
      dateToTimeMenu: false
    };
  },
  validations() {
    let validations = {
      form: {
        course: {
          required
        }
      }
    };

    if (this.restrictDateFrom) {
      validations.form = {
        ...validations.form,
        dateFrom: {
          required
        },
        dateFromTime: { required }
      };
    }

    if (this.restrictDateTo) {
      validations.form = {
        ...validations.form,
        dateTo: {
          required
        },
        dateToTime: { required }
      };

      if (this.restrictDateFrom) {
        validations.form.dateTo.minDateTime = minDateTime('dateFrom', 'dateFromTime', 'dateToTime');
      }
    }

    return validations;
  },
  computed: {
    ...mapState({
      unAssignedCourses: state => state.groupAssignment.unAssignedCourses
    }),
    dateFromFormatted() {
      return this.form.dateFrom ? moment(this.form.dateFrom).format('DD.MM.YYYY') : '';
    },
    dateToFormatted() {
      return this.form.dateTo ? moment(this.form.dateTo).format('DD.MM.YYYY') : '';
    },
    courseErrors() {
      const errors = [];
      if (!this.$v.form.course.$dirty) {
        return errors;
      }
      if (!this.$v.form.course.required) {
        errors.push('Szkolenie jest wymagane');
      }
      return errors;
    },
    dateFromDateErrors() {
      const errors = [];
      if (this.$v.form.dateFrom) {
        if (!this.$v.form.dateFrom.$dirty) {
          return errors;
        }
        if (!this.$v.form.dateFrom.required) {
          errors.push('Data rozpoczęcia jest wymagana');
        }
      }
      return errors;
    },
    dateFromTimeErrors() {
      const errors = [];
      if (this.$v.form.dateFromTime) {
        if (!this.$v.form.dateFromTime.$dirty) {
          return errors;
        }
        if (!this.$v.form.dateFromTime.required) {
          errors.push('Godzina rozpoczęcia jest wymagana');
        }
      }
      return errors;
    },
    dateToDateErrors() {
      const errors = [];
      if (this.$v.form.dateTo) {
        if (!this.$v.form.dateTo.$dirty) {
          return errors;
        }
        if (!this.$v.form.dateTo.required) {
          errors.push('Data zakończenia jest wymagana');
        }
        if (this.$v.form.dateFrom) {
          if (!this.$v.form.dateTo.minDateTime) {
            errors.push('Zakończenie nie może być wcześniejsze niż rozpoczęcie');
          }
        }
      }
      return errors;
    },
    dateToTimeErrors() {
      const errors = [];
      if (this.$v.form.dateToTime) {
        if (!this.$v.form.dateToTime.$dirty) {
          return errors;
        }
        if (!this.$v.form.dateToTime.required) {
          errors.push('Godzina zakończenia jest wymagana');
        }
      }
      return errors;
    },
    formValid() {
      return !this.$v.form.$invalid && !this.$v.form.$pending;
    },
    formChanged() {
      return this.$v.$anyDirty;
    }
  },
  watch: {
    search(val) {
      this.getUnassignedCourses({ groupId: this.group.id, query: val });
    }
  },
  methods: {
    ...mapActions(['getUnassignedCourses', 'assignCourse']),
    async loadProperties() {
      this.dialog.loading = true;
      this.$v.$reset();
      this.restrictDateFrom = false;
      this.restrictDateTo = false;
      this.form.course = null;
      this.form.dateFrom = null;
      this.form.dateFromTime = null;
      this.form.dateTo = null;
      this.form.dateToTime = null;
      this.form.keepCourseOngoing = false;

      await this.getUnassignedCourses({ groupId: this.group.id, query: '' });
      this.dialog.loading = false;
    },
    async addAssignment() {
      this.$v.$touch();

      if (this.formValid) {
        this.dialog.loading = true;
        await this.assignCourse({
          groupId: this.group.id,
          courseId: this.form.course,
          dateFrom: this.restrictDateFrom
            ? moment(`${this.form.dateFrom} ${this.form.dateFromTime}`, 'YYYY-MM-DD HH:mm').toISOString(true)
            : null,
          dateTo: this.restrictDateTo
            ? moment(`${this.form.dateTo} ${this.form.dateToTime}`, 'YYYY-MM-DD HH:mm').toISOString(true)
            : null,
          keepCourseOngoing: this.restrictDateTo 
            ? this.form.keepCourseOngoing
            : false,
          page: this.options.page,
          itemsPerPage: this.options.itemsPerPage,
          search: this.options.search
        });

        this.dialog.value = false;
        this.dialog.loading = false;
      }
    }
  }
};
</script>
